import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  ArrowLeft, BarChart3, Search as SearchIcon, ChevronRight, ChevronDown,
  PhoneIncoming, Bot, MapPin, Clock,
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * /admin/ai-voice-dashboard
 *
 * Live audit of every call the AI voice agent has handled. Pulls from
 * /admin/ai-voice-dashboard which projects ai_voice_calls (caller, state,
 * turn count, full transcript) sorted by most-recent-first.
 */

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch { return iso; }
};

const VoiceAgentDashboardPage = () => {
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const canAccess = isAdmin || (user?.feature_toggles || {}).admin_ai_voice_dashboard === true;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [q, setQ] = useState('');

  const load = async () => {
    try {
      const r = await api.get('/admin/ai-voice-dashboard');
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error('Could not load voice agent calls');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canAccess) return undefined;
    load();
    const t = setInterval(load, 15000); // light auto-refresh every 15s
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [canAccess]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    const has = (s) => s && String(s).toLowerCase().includes(needle);
    return items.filter((it) =>
      has(it.from_number) || has(it.state) || has(it.first_user_message) || has(it.call_sid)
    );
  }, [items, q]);

  if (!canAccess) {
    return (
      <div className="min-h-screen bg-background p-8 flex flex-col items-center justify-center text-center">
        <p className="text-lg font-medium">Admin only</p>
        <Button className="mt-4" onClick={() => navigate('/')}>Back</Button>
      </div>
    );
  }

  const totalCalls = items.length;
  const totalTurns = items.reduce((acc, it) => acc + (it.user_turns || 0), 0);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#d4e4cf] via-[#e8ede6] to-[#c9d4c3] dark:from-[#1a1a1a] dark:via-[#0d0d0d] dark:to-[#000000] pb-24" data-testid="voice-agent-dashboard">
      <div className="max-w-3xl mx-auto p-3">
        <div className="flex items-center gap-2 mb-3">
          <Button size="icon" variant="ghost" onClick={() => navigate('/admin/ai-training/voice-agent')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-[#3b82f6]" />
            Voice Agent Dashboard
          </h1>
        </div>

        {/* Quick stats */}
        <div className="grid grid-cols-2 gap-2 mb-3">
          <Card className="rounded-xl">
            <CardContent className="pt-4 pb-3">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Calls handled</p>
              <p className="text-2xl font-bold">{totalCalls}</p>
            </CardContent>
          </Card>
          <Card className="rounded-xl">
            <CardContent className="pt-4 pb-3">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Caller turns</p>
              <p className="text-2xl font-bold">{totalTurns}</p>
            </CardContent>
          </Card>
        </div>

        <div className="relative mb-3">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by caller, state, or first message…"
            className="w-full pl-9 pr-3 py-2 rounded-xl bg-white/70 dark:bg-[#1a1a1a]/70 border border-gray-200 dark:border-gray-700 text-sm focus:outline-none focus:ring-2 focus:ring-[#3b82f6]"
            data-testid="dashboard-search"
          />
        </div>

        <Card className="rounded-2xl">
          <CardContent className="pt-4 space-y-2">
            {loading && <p className="text-sm text-muted-foreground py-6 text-center">Loading…</p>}
            {!loading && filtered.length === 0 && (
              <p className="text-sm text-muted-foreground py-6 text-center">
                {q ? 'No matches.' : 'No AI voice agent calls yet — your dashboard will populate as soon as someone calls option 4.'}
              </p>
            )}
            {!loading && filtered.map((it) => {
              const open = expanded === it.call_sid;
              return (
                <div
                  key={it.call_sid}
                  className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white/70 dark:bg-[#1a1a1a]/70"
                  data-testid={`voice-call-row-${it.call_sid}`}
                >
                  <button
                    onClick={() => setExpanded((cur) => (cur === it.call_sid ? null : it.call_sid))}
                    className="w-full flex items-center gap-3 p-3 text-left"
                  >
                    {open
                      ? <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      : <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />}
                    <div className="w-9 h-9 rounded-full bg-[#3b82f6]/15 text-[#3b82f6] flex items-center justify-center flex-shrink-0">
                      <PhoneIncoming className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {it.from_number || 'Unknown caller'}
                      </p>
                      <p className="text-xs text-muted-foreground flex items-center flex-wrap gap-x-2 gap-y-0.5">
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{fmtDate(it.updated_at || it.created_at)}</span>
                        {it.state && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{it.state}</span>}
                        <span>· {it.user_turns} caller turn{it.user_turns === 1 ? '' : 's'}</span>
                      </p>
                      {it.first_user_message && (
                        <p className="text-xs italic text-muted-foreground truncate mt-0.5">
                          "{it.first_user_message}"
                        </p>
                      )}
                    </div>
                  </button>
                  {open && (
                    <div className="px-4 pb-3 pt-1 border-t border-gray-100 dark:border-gray-800 space-y-2">
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Transcript</p>
                      <div className="space-y-1.5 max-h-96 overflow-auto">
                        {(it.messages || []).length === 0 && (
                          <p className="text-xs text-muted-foreground italic">No turns recorded.</p>
                        )}
                        {(it.messages || []).map((m, idx) => {
                          const isUser = m.role === 'user';
                          return (
                            <div key={idx} className={`flex flex-col ${isUser ? 'items-start' : 'items-end'}`}>
                              <div className={`max-w-[85%] min-w-0 rounded-lg px-2.5 py-1.5 text-xs ${
                                isUser
                                  ? 'bg-gray-100 dark:bg-[#0d0d0d]'
                                  : 'bg-[#3b82f6]/15 text-[#0b3a85] dark:text-[#9bbcff]'
                              }`}>
                                <p className="text-[10px] uppercase tracking-wider opacity-70 mb-0.5 flex items-center gap-1">
                                  {isUser ? 'Caller' : (<><Bot className="w-3 h-3" />Agent</>)}
                                </p>
                                <p className="whitespace-pre-wrap break-words">{m.content}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <p className="text-[10px] text-muted-foreground font-mono break-all pt-1">{it.call_sid}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default VoiceAgentDashboardPage;
